import {Component, inject, Input, OnInit, ViewChild, ViewContainerRef} from '@angular/core';
import {ChartDatasetExtended, MeasurementData} from "../base-measurement-chart";
import {DataType, DayOfWeekMeasurementsResult, HourValue} from "@flowmaps-com/flowmaps-typescriptmodels";
import {DashboardContext} from "../../dashboard/dashboard.context";
import {ChartOptions, ChartOptionType} from "../../dashboard/dashboard.types";
import {AppContext} from "../../../app-context";
import {lodash, removeItem} from "../../../common/utils";
import {SentenceCasePipe} from "../../../common/sentence-case.pipe";
import {ActiveElement, Chart, ChartEvent} from "chart.js";
import {cloneDeep} from "lodash";
import {TranslateDirective} from "../../../common/utils/translate.directive";
import {Handler} from "../../../common/handler";
import {View} from "../../../common/view";
import {ChartModalOptions, ChartUtilsService} from "../chart-utils.service";
import {ChartDataProvider} from "../../../utils/chart-data-provider";
import {MeasurementsHandlerOpenModalCommand} from "../../measurements-component/measurements-handler.component";
import moment from "moment";

@Component({
    selector: 'app-day-of-week-chart',
    // Keep the meter dashboard's default measurement input on the Handler subclass in JIT builds.
    inputs: ['selectedMeasurement'],
    templateUrl: './day-of-week-chart.component.html',
    styleUrls: ['./day-of-week-chart.component.scss']
})
@Handler()
export class DayOfWeekChart extends View implements OnInit {
    private chartUtils: ChartUtilsService = inject(ChartUtilsService);
    appContext = AppContext;
    dashboardContext = DashboardContext;

    @Input() showInReport: boolean;
    @Input() reportMode: boolean = false;
    @Input() reportModeLabel: string;
    @Input() officialHolidaysOnly: boolean = false;
    @Input() dataType: DataType;
    private _selectedMeasurement: DataType;

    @Input()
    set selectedMeasurement(value: DataType) {
        this._selectedMeasurement = value;
        if (this.options) {
            this.measurementTypeChanged(value || (this.showIntermediateData
                ? DataType.electricityIntermediateConsumption : DataType.electricityConsumption));
        }
    }

    get selectedMeasurement(): DataType {
        return this._selectedMeasurement;
    }
    private _inputDataset: DayOfWeekMeasurementsResult;

    _rawDataset: DayOfWeekMeasurementsResult;
    records: DayOfWeekChartRecord[] = [];
    estimatedRecords: DayOfWeekChartRecord[] = [];
    options: ChartOptions;
    modalOptions: ChartModalOptions = {
        fullScreen: false
    }
    chartDataProvider: ChartDataProvider = new ChartDataProvider();
    @ViewChild('container', {read: ViewContainerRef}) container: ViewContainerRef;
    private showIntermediateData: boolean;

    colors = DashboardContext.weekdayColors;

    // TODO: What to do with this, maybe generate an unique ID for each View
    private uniqueId = lodash.uniqueId();
    getInputId = (id: string) => `${this.uniqueId}-${id}`;

    getDayOfWeekInputId = (dayOfWeek: string) => this.getInputId('checkbox-' + dayOfWeek + '-' + this.optionsType());

    isDayOfWeekSelected = (dayOfWeek: string) => this.options.selectedDays.includes(dayOfWeek);

    allDaysSelected = (): boolean => this.options.selectedDays.length === DashboardContext.weekdaysSorted.length;

    ngOnInit() {
        this.subscribeTo("getChartOptions", ChartOptionType.DaysOfWeek).subscribe((opts: ChartOptions) => {
            opts = cloneDeep(opts);
            opts.showAllDays = opts.showAllDays === undefined ? true : opts.showAllDays;
            opts.selectedDataType = this.dataType || (this.selectedMeasurement
                ? this.options?.selectedDataType || this.selectedMeasurement : opts.selectedDataType)
                || DataType.electricityConsumption;
            opts.selectedDays = opts.selectedDays && opts.selectedDays.length > 0 ? opts.selectedDays : cloneDeep(DashboardContext.weekdaysSorted);
            opts.officialHolidaysOnly = this.officialHolidaysOnly || !!opts.officialHolidaysOnly;
            this.options = opts;
            if (this.usesDirectDataset()) {
                this.setData(this._inputDataset);
            }
        });
        this.subscribeTo("onlyMetersSelected").subscribe(d => {
            if (!this.hasFixedDataType() && !this.selectedMeasurement && this.showIntermediateData !== d) {
                this.measurementTypeChanged(d ? DataType.electricityIntermediateConsumption : DataType.electricityConsumption);
            }
            this.showIntermediateData = d;
        })
        this.subscribeTo("getDayOfWeekMeasurements").subscribe((d: DayOfWeekMeasurementsResult) => {
            if (!this.usesDirectDataset()) {
                this.setData(d);
            }
        });
    }

    @Input()
    set data(modalOptions: ChartModalOptions) {
        this.modalOptions = modalOptions;
    }

    @Input()
    set dataset(dataset: DayOfWeekMeasurementsResult) {
        this._inputDataset = dataset;
        if (this.options && this.usesDirectDataset()) {
            this.setData(dataset);
        }
    }

    get y2AxisTitle(): string {
        return this.options?.selectedWeatherTypes?.length === 1
            ? DashboardContext.getMeasurementUnit(this.options.selectedWeatherTypes[0]) : null;
    }

    @Input()
    set allDaysByDefault(value: boolean) {
        this.toggleAllDays(value);
    }

    toggleAllDays = (selected: boolean) => {
        this.options = this.options || {};
        this.options.selectedDays = selected ? cloneDeep(DashboardContext.weekdaysSorted) : [];
        this.options.showAllDays = !this.options.selectedDays.length;
        this.setData(this._rawDataset);
    }

    toggleDayOfWeek = (dayOfWeek: string) => {
        if (this.isDayOfWeekSelected(dayOfWeek)) {
            this.options.selectedDays = removeItem(this.options.selectedDays, dayOfWeek)
        } else {
            this.options.selectedDays.push(dayOfWeek);
        }
        this.options.showAllDays = !this.options.selectedDays.length;
        this.setData(this._rawDataset);
    }

    toggleOfficialHolidaysOnly = (selected: boolean) => {
        this.options.officialHolidaysOnly = selected;
        this.setData(this._rawDataset);
    }

    get showAsLineChart(): boolean {
        return !this.options.showAllDays && (!this.options.selectedDays?.length || this.options.selectedDays.length > 1);
    }

    setData(result: DayOfWeekMeasurementsResult) {
        this._rawDataset = result;
        this.createData(result);
        const value: MeasurementData = this.options.showAllDays ? this.getDatasetForAllDays() : {
            labels: lodash.range(0, 24).map(h => `${h}:00`),
            datasets: this.records
                .filter(r => this.options.officialHolidaysOnly ? true : this.options.selectedDays.includes(r.filterKey))
                .map(r => ({
                    entityId: null,
                    measurementType: this.options.selectedDataType,
                    dataset: this.getDataset(false, r)
                }))
        };
        this.chartDataProvider.emit(value);
    }

    createData = (result: DayOfWeekMeasurementsResult) => {
        this.records = [];
        this.estimatedRecords = [];
        if (this.options?.officialHolidaysOnly) {
            const measurements = result?.holidayMeasurements;
            if (measurements) {
                Object.entries(measurements).forEach(([date, holidayResult]) => {
                    const dayOfWeek = DashboardContext.weekdaysSorted[(moment(date).day() + 6) % 7];
                    this.records.push({
                        dayOfWeek,
                        filterKey: date,
                        values: this.mergeHourValues(holidayResult)
                    });
                });
                this.records = lodash.sortBy(this.records, r => r.filterKey);
            }
            return;
        }

        const measurements = result?.measurements;
        if (measurements) {
            Object.entries(measurements).forEach(([dayOfWeek, dayResult]) => {
                this.records.push({
                    dayOfWeek,
                    filterKey: dayOfWeek,
                    values: this.mergeHourValues(dayResult)
                });
            });
            this.records = lodash.sortBy(this.records, r => DashboardContext.weekdaysSorted.indexOf(r.dayOfWeek));
        }
    }

    private getDataset = (estimated: boolean, data: DayOfWeekChartRecord): ChartDatasetExtended => {
        const color = this.options?.officialHolidaysOnly
            ? this.getHolidayColor(data)
            : this.showAsLineChart
                ? this.colors[data.dayOfWeek]
                : DashboardContext.getMeasurementColor(DashboardContext.stacks.currentPeriod, this.options.selectedDataType);
        const label = this.getRecordLabel(data);
        return {
            type: this.showAsLineChart ? "line" : null,
            yAxisID: "lines",
            label: estimated ? `${label} (${TranslateDirective.getTranslation("estimated", true)})` : label,
            data: data.values.map(v => v.value),
            order: 1,
            borderColor: color,
            backgroundColor: estimated ? "#FFFFFF" : color,
            pointBorderColor: color,
            measurementType: this.options.selectedDataType,
            stack: this.options?.officialHolidaysOnly ? undefined : data.dayOfWeek,
            tooltip: {
                formatter: this.measurementTooltipFormatter(),
                labelOverride: this.measurementName(this.options.selectedDataType),
                data: data.values.map(v => v.value),
                stack: DashboardContext.stacks.currentPeriod,
                estimated: estimated
            }
        }
    }

    measurementTypes = (): DataType[] => {
        // Also exclude reactive feed-in when an older saved selection requests it.
        if ([DataType.electricityFeedInReactive, DataType.electricityFeedInReactiveOffPeak]
            .includes(this.options.selectedDataType)) return [];
        if (this.options.selectedDataType === DataType.electricityConsumption) {
            return [DataType.electricityConsumption, DataType.electricityConsumptionOffPeak];
        }
        if (this.options.selectedDataType === DataType.electricityFeedIn) {
            return [DataType.electricityFeedIn, DataType.electricityFeedInOffPeak];
        }
        if (this.options.selectedDataType === DataType.electricityConsumptionReactive) {
            return [DataType.electricityConsumptionReactive, DataType.electricityConsumptionReactiveOffPeak];
        }
        if (this.options.selectedDataType === DataType.electricityPower) {
            return [DataType.electricityConsumption, DataType.electricityConsumptionOffPeak];
        }
        return [this.options.selectedDataType];
    };

    openModal = () => {
        this.sendCommandAndForget("openChartInModal", <MeasurementsHandlerOpenModalCommand>{
            component: DayOfWeekChart,
            chartOptions: {
                [ChartOptionType.DaysOfWeek]: this.options
            },
            chartModalData: this.modalOptions,
            container: this.container
        });
    }

    closeModal = () => this.sendCommandAndForget("closeModal");

    measurementUnit = (): string => `${DashboardContext.getMeasurementUnit(this.options.selectedDataType,
        this.hasFixedDataType() || !this.selectedMeasurement)}`;

    private measurementTooltipFormatter = () => !this.hasFixedDataType() && this.selectedMeasurement
        && this.options.selectedDataType === DataType.heatConsumption
        ? (_context, value: number) => `${this.chartUtils.transformDecimal(value)} ${this.measurementUnit()}`
        : this.chartUtils.getCustomTooltipFormatter(this.options.selectedDataType, false);

    optionsType = (): ChartOptionType => ChartOptionType.DaysOfWeek;

    measurementName = (measurementType: DataType, translate: boolean = true): string => {
        return AppContext.entityPerformanceMeasurementName(measurementType, translate);
    }

    dropdownFormatter = (measurementType: DataType) => this.measurementName(measurementType, false);

    possibleDataTypes = (): DataType[] =>
        [DataType.electricityConsumption, DataType.electricityFeedIn, DataType.electricityConsumptionReactive, DataType.electricityGrossProduction, DataType.electricityPower]
            .concat([DataType.gasConsumption, DataType.heatConsumption, DataType.waterConsumption, DataType.coolingConsumption, DataType.chargePointConsumption])
            .concat([DataType.electricityIntermediateConsumption, DataType.waterIntermediateConsumption])
            .concat(AppContext.isAdmin() ? [DataType.gasConsumptionUncorrected] : [])
            .concat(AppContext.weatherTypes);

    measurementTypeChanged = (m: DataType) => {
        this.options.selectedDataType = this.hasFixedDataType() ? this.dataType : m;
        this.setData(this._rawDataset);
    }

    private hasFixedDataType(): boolean {
        return !!this.dataType;
    }

    private usesDirectDataset(): boolean {
        return !!this._inputDataset;
    }

    private getDatasetForAllDays(): MeasurementData {
        const measurementColor = DashboardContext.getMeasurementColor(DashboardContext.stacks.currentPeriod, this.options.selectedDataType);
        const aggregationMethod = this.options.selectedDataType === DataType.electricityPower
            ? lodash.max : AppContext.isWeatherType(this.options.selectedDataType)
                ? lodash.mean : lodash.sum;
        const records = this.records.map(r => aggregationMethod(r.values.map(v => v.value)));
        const labels = this.records.map(r => this.getRecordLabel(r));
        const colors = this.options?.officialHolidaysOnly
            ? this.records.map(record => this.getHolidayColor(record))
            : labels.map(() => measurementColor);
        return {
            labels: labels.map(l => this.options?.officialHolidaysOnly ? l : l.substring(0, 2)),
            datasets: [{
                measurementType: this.options.selectedDataType,
                estimated: false,
                dataset: {
                    yAxisID: "lines",
                    label: this.measurementName(this.options.selectedDataType),
                    data: records,
                    order: 1,
                    borderColor: colors,
                    backgroundColor: colors,
                    pointBorderColor: colors,
                    measurementType: this.options.selectedDataType,
                    stack: DashboardContext.stacks.currentPeriod,
                    tooltipLabels: labels,
                    tooltip: {
                        formatter: this.measurementTooltipFormatter(),
                        labelOverride: this.measurementName(this.options.selectedDataType),
                        data: records,
                        estimated: false
                    }
                }
            }]
        }
    }

    navigateToDay = (event: ChartEvent, elements: ActiveElement[], chart: Chart) => {
        const points = chart.getElementsAtEventForMode(event.native, 'nearest', {intersect: true}, true);
        if (points && elements.length > 0) {
            if (this.options.showAllDays) {
                const clickedRecord: DayOfWeekChartRecord = this.records[points[0].index];
                this.options.selectedDays = [clickedRecord.filterKey];
                this.options.showAllDays = false;
                this.setData(this._rawDataset);
            }
        } else {
            if (!this.options.showAllDays) {
                this.options.showAllDays = true;
                this.options.selectedDays = [];
                this.setData(this._rawDataset);
            }
        }
    }

    onHover = (event: ChartEvent, elements: ActiveElement[], chart: Chart) => {
        const target: any = event.native.target;
        if (this.options.showAllDays) {
            target.style.cursor = elements.length > 0 ? "pointer" : "auto";
        } else {
            target.style.cursor = elements.length > 0 ? "auto" : "zoom-out";
        }
    }

    private mergeHourValues(result: {[P in DataType]?: HourValue[]}): HourValue[] {
        const dataPerHour: { [hour: number]: HourValue } = {};
        Object.entries(result || {})
            .filter(([dataType]) => this.measurementTypes().includes(dataType as DataType))
            .forEach(([, values]) => {
                values.forEach(v => {
                    const val = dataPerHour[v.hour] || {hour: v.hour, value: 0};
                    val.value += v.value;
                    dataPerHour[v.hour] = val;
                });
            });
        return Object.values(dataPerHour);
    }

    private getRecordLabel(record: DayOfWeekChartRecord): string {
        return this.options?.officialHolidaysOnly
            ? TranslateDirective.getTranslation(this.getHolidayLabel(record.filterKey), true)
            : TranslateDirective.getTranslation(SentenceCasePipe.format(record.dayOfWeek), true);
    }

    private getHolidayLabel(date: string): string {
        const holidayLabels = this.getOfficialHolidaysForYear(moment(date).year());
        return holidayLabels[date] || moment(date).format("D MMM YYYY");
    }

    private getHolidayColor(record: DayOfWeekChartRecord): string {
        const index = Math.max(0, this.records.findIndex(r => r.filterKey === record.filterKey));
        const hue = (index * 47) % 360;
        return `hsl(${hue} 68% 52%)`;
    }

    private getOfficialHolidaysForYear(year: number): Record<string, string> {
        const easterSunday = this.getEasterSunday(year);
        const kingsDay = this.getKingsDay(year);
        return {
            [moment({year, month: 0, date: 1}).format("YYYY-MM-DD")]: "New Year's Day",
            [easterSunday.format("YYYY-MM-DD")]: "Easter Sunday",
            [easterSunday.clone().add(1, "day").format("YYYY-MM-DD")]: "Easter Monday",
            [kingsDay.format("YYYY-MM-DD")]: "King's Day",
            [easterSunday.clone().add(39, "days").format("YYYY-MM-DD")]: "Ascension Day",
            [easterSunday.clone().add(49, "days").format("YYYY-MM-DD")]: "Pentecost Sunday",
            [easterSunday.clone().add(50, "days").format("YYYY-MM-DD")]: "Pentecost Monday",
            [moment({year, month: 11, date: 25}).format("YYYY-MM-DD")]: "Christmas Day",
            [moment({year, month: 11, date: 26}).format("YYYY-MM-DD")]: "Boxing Day"
        };
    }

    private getKingsDay(year: number): moment.Moment {
        const kingsDay = moment({year, month: 3, date: 27});
        return kingsDay.day() === 0 ? kingsDay.subtract(1, "day") : kingsDay;
    }

    private getEasterSunday(year: number): moment.Moment {
        const a = year % 19;
        const b = Math.floor(year / 100);
        const c = year % 100;
        const d = Math.floor(b / 4);
        const e = b % 4;
        const f = Math.floor((b + 8) / 25);
        const g = Math.floor((b - f + 1) / 3);
        const h = (19 * a + b - d - g + 15) % 30;
        const i = Math.floor(c / 4);
        const k = c % 4;
        const l = (32 + 2 * e + 2 * i - h - k) % 7;
        const m = Math.floor((a + 11 * h + 22 * l) / 451);
        const month = Math.floor((h + l - 7 * m + 114) / 31);
        const day = ((h + l - 7 * m + 114) % 31) + 1;
        return moment({year, month: month - 1, date: day});
    }
}

interface DayOfWeekChartRecord {
    dayOfWeek: string;
    filterKey: string;
    values: HourValue[];
}
